import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * With hash history, query strings often live on the hash (`#/path?k=v`), not `location.search`.
 * Prefer `?...` from `location.hash` when present; otherwise fall back to `location.search`.
 *
 * @param {{ search?: string, hash?: string }} [location]
 * @returns {string}
 */
export const getCabinetLocationSearch = function getCabinetLocationSearch() {
  let location = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const hash = typeof location.hash === 'string' ? location.hash : '';
  const q = hash.indexOf('?');
  const hashQuery = q === -1 ? '' : hash.slice(q);
  // Prefer the in-app hash query so a stray document `?…` (e.g. SSO) cannot shadow cabinet
  // deep links that live on `#/cabinets/…?filters…`.
  if (hashQuery.length > 1) {
    return hashQuery;
  }
  if (typeof location.search === 'string' && location.search.length > 1) {
    return location.search;
  }
  return '';
};

/**
 * @param {string} [search]
 * @returns {{ filterId: string } | null}
 */
export const getCabinetSavedViewParamsFromSearch = search => {
  if (!search || typeof search !== 'string') {
    return null;
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (params.get('view') !== 'saved') {
    return null;
  }
  const filterId = params.get('filterId');
  if (!filterId || !String(filterId).trim()) {
    return null;
  }
  return {
    filterId: String(filterId).trim()
  };
};

/**
 * @param {{ search?: string, hash?: string }} [location]
 * @returns {boolean}
 */
export const hasCabinetSavedViewDeepLink = function hasCabinetSavedViewDeepLink() {
  var _getCabinetSavedViewP;
  let location = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Boolean((_getCabinetSavedViewP = getCabinetSavedViewParamsFromSearch(getCabinetLocationSearch(location))) === null || _getCabinetSavedViewP === void 0 ? void 0 : _getCabinetSavedViewP.filterId);
};

/**
 * User `content.savedViews[cabinetIdentifier][filterId]` row for saved-view URLs.
 *
 * @param {Record<string, unknown>} [savedViews]
 * @param {string} cabinetIdentifier
 * @param {string} filterId
 * @returns {Record<string, unknown> | null}
 */
export const resolveCabinetSavedViewRow = (savedViews, cabinetIdentifier, filterId) => {
  if (!savedViews || !cabinetIdentifier || !filterId) {
    return null;
  }
  const byFilter = savedViews[cabinetIdentifier];
  if (!byFilter || typeof byFilter !== 'object' || Array.isArray(byFilter)) {
    return null;
  }
  const direct = byFilter[filterId];
  if (direct && typeof direct === 'object') {
    return direct;
  }
  const keys = Object.keys(byFilter);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const row = byFilter[key];
    if (row && typeof row === 'object' && row.id != null && String(row.id) === String(filterId)) {
      return row;
    }
  }
  return null;
};

/**
 * One cabinet filter selection row, matching saved-view storage (array of ids/values).
 * Cells may be `null` when the selection is cleared (“No selection”).
 *
 * @param {unknown} tuple
 * @returns {Array<string|null>}
 */
const normalizeSelectionTuple = tuple => {
  if (!Array.isArray(tuple)) {
    if (tuple === null || tuple === '') {
      return [null];
    }
    return [String(tuple)];
  }
  return tuple.map(x => {
    if (x === null || x === '') {
      return null;
    }
    return String(x);
  });
};

/**
 * Normalize a saved-view-shaped `filters` object: each value is an array of selection tuples.
 *
 * @param {Record<string, unknown>} [filters]
 * @returns {Record<string, Array<Array<string|null>>>}
 */
export const normalizeSavedViewStyleCabinetFilters = function normalizeSavedViewStyleCabinetFilters() {
  let filters = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const out = {};
  Object.entries(filters).forEach(_ref => {
    let _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      value = _ref2[1];
    if (!Array.isArray(value)) {
      return;
    }
    out[key] = value.map(row => normalizeSelectionTuple(row));
  });
  return out;
};

/**
 * Parse `location.search` from a hash route (e.g. `?projectTitle=Foo`) into cabinet
 * index `filters` entries. Only keys listed in `filterDefinitions[].index` are read;
 * other query params are ignored.
 *
 * Each query value becomes one cabinet filter selection in the same shape as user
 * saved views: `[[value], ...]`. Repeat the same key for multiple selections:
 * `?tag=a&tag=b`. Non-empty values are strings; empty values (`?tag=` or `…=`) become
 * `[null]` in the tuple, matching “No selection” in the cabinet filter UI.
 *
 * Also reads bracket params `filters[<index>][<row>][<col>]=…` (same shape as map / GET
 * URLs), e.g. `filters[locationTitle][0][0]=` for an explicit empty location filter.
 *
 * @param {string} [search]
 * @param {Array<{ index?: string }>} [filterDefinitions]
 * @returns {Record<string, Array<Array<string|null>>>}
 */
const FILTER_BRACKET_KEY_RE = /^filters\[([^\]]+)]\[(\d+)]\[(\d+)]$/;

/**
 * @param {URLSearchParams} params
 * @param {Set<string>} allowed
 * @returns {Record<string, Array<Array<string|null>>>}
 */
const getCabinetFiltersFromBracketSearchParams = (params, allowed) => {
  /** @type {Record<string, Map<number, Map<number, string>>>} */
  const byIndex = {};
  params.forEach((value, paramKey) => {
    const m = paramKey.match(FILTER_BRACKET_KEY_RE);
    if (!m) {
      return;
    }
    const idx = m[1];
    if (!allowed.has(idx)) {
      return;
    }
    const rowI = Number(m[2]);
    const colJ = Number(m[3]);
    if (!byIndex[idx]) {
      byIndex[idx] = new Map();
    }
    const rowMap = byIndex[idx];
    if (!rowMap.has(rowI)) {
      rowMap.set(rowI, new Map());
    }
    rowMap.get(rowI).set(colJ, value);
  });
  const out = {};
  Object.keys(byIndex).forEach(idx => {
    const rowMap = byIndex[idx];
    const sortedRows = [...rowMap.keys()].sort((a, b) => a - b);
    out[idx] = sortedRows.map(ri => {
      const colMap = rowMap.get(ri);
      const sortedCols = [...colMap.keys()].sort((a, b) => a - b);
      return sortedCols.map(cj => {
        const v = colMap.get(cj);
        return v === '' || v == null ? null : String(v);
      });
    });
  });
  return out;
};
export const getCabinetFiltersFromSearch = function getCabinetFiltersFromSearch(search) {
  let filterDefinitions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  if (!search || !filterDefinitions.length) {
    return {};
  }
  const allowed = new Set(filterDefinitions.map(f => f.index).filter(idx => typeof idx === 'string' && idx));
  if (!allowed.size) {
    return {};
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const out = getCabinetFiltersFromBracketSearchParams(params, allowed);
  allowed.forEach(key => {
    if (out[key]) {
      return;
    }
    const flatValues = params.getAll(key);
    if (!flatValues.length) {
      return;
    }
    out[key] = flatValues.map(v => [v === '' || v == null ? null : String(v)]);
  });
  return out;
};

/**
 * Builds `filters` the same way selecting a saved cabinet view does: clear full-text
 * and AI search, reset `metaDeleted`, drop prior `filterDefinitions` selections, then
 * apply `savedStyleFilters` (from a saved view blob or from deep-link parsing).
 *
 * @param {{
 *   existingFilters?: Record<string, unknown>,
 *   savedStyleFilters?: Record<string, unknown>,
 *   formTypeIdentifiers: string[],
 *   cabinetIdentifier: string,
 *   filterDefinitions?: Array<{ index?: string }>,
 * }} args
 * @returns {Record<string, unknown>}
 */
export const mergeCabinetSavedStyleFilters = _ref3 => {
  var _existingFilters$form;
  let _ref3$existingFilters = _ref3.existingFilters,
    existingFilters = _ref3$existingFilters === void 0 ? {} : _ref3$existingFilters,
    _ref3$savedStyleFilte = _ref3.savedStyleFilters,
    savedStyleFilters = _ref3$savedStyleFilte === void 0 ? {} : _ref3$savedStyleFilte,
    formTypeIdentifiers = _ref3.formTypeIdentifiers,
    cabinetIdentifier = _ref3.cabinetIdentifier,
    _ref3$filterDefinitio = _ref3.filterDefinitions,
    filterDefinitions = _ref3$filterDefinitio === void 0 ? [] : _ref3$filterDefinitio;
  const defIndexes = new Set(filterDefinitions.map(f => f.index).filter(idx => typeof idx === 'string' && idx));
  const normalizedSaved = normalizeSavedViewStyleCabinetFilters(savedStyleFilters);
  const next = _objectSpread(_objectSpread({}, existingFilters), {}, {
    text: '',
    aiSearch: '',
    // Match handleClear / saved-view apply: always reset deleted visibility.
    metaDeleted: 0
  });
  defIndexes.forEach(idx => {
    if (next[idx] !== undefined) {
      delete next[idx];
    }
  });
  return _objectSpread(_objectSpread({}, next), {}, {
    formTypeIdentifiers: (_existingFilters$form = existingFilters.formTypeIdentifiers) !== null && _existingFilters$form !== void 0 ? _existingFilters$form : formTypeIdentifiers,
    cabinetIdentifier
  }, normalizedSaved);
};

/**
 * Build the index query object passed to `cabinetActions.serverReplaceView` — the same
 * route used when applying a saved cabinet filter. Deep links parse into `savedView.filters`;
 * saved rows pass `savedView.filters` and `savedView.sort` from storage.
 *
 * @param {{
 *   indexQuery?: Record<string, unknown>,
 *   initialIndexQuery?: Record<string, unknown>,
 *   savedView: { filters?: Record<string, unknown>, sort?: unknown },
 *   formTypeIdentifiers: string[],
 *   cabinetIdentifier: string,
 *   filterDefinitions?: Array<{ index?: string }>,
 *   mergeExistingCabinetFilters?: boolean,
 * }} args
 * @returns {Record<string, unknown>}
 */
export const buildCabinetReplaceViewQuery = _ref4 => {
  let indexQuery = _ref4.indexQuery,
    _ref4$initialIndexQue = _ref4.initialIndexQuery,
    initialIndexQuery = _ref4$initialIndexQue === void 0 ? {} : _ref4$initialIndexQue,
    savedView = _ref4.savedView,
    formTypeIdentifiers = _ref4.formTypeIdentifiers,
    cabinetIdentifier = _ref4.cabinetIdentifier,
    _ref4$filterDefinitio = _ref4.filterDefinitions,
    filterDefinitions = _ref4$filterDefinitio === void 0 ? [] : _ref4$filterDefinitio,
    _ref4$mergeExistingCa = _ref4.mergeExistingCabinetFilters,
    mergeExistingCabinetFilters = _ref4$mergeExistingCa === void 0 ? true : _ref4$mergeExistingCa;
  const base = indexQuery && Object.keys(indexQuery).length ? indexQuery : _objectSpread({}, initialIndexQuery);
  const existingForMerge = mergeExistingCabinetFilters ? base.filters || {} : {};
  const mergedFilters = mergeCabinetSavedStyleFilters({
    existingFilters: existingForMerge,
    savedStyleFilters: savedView.filters || {},
    formTypeIdentifiers,
    cabinetIdentifier,
    filterDefinitions
  });
  const sort = savedView.sort !== undefined ? savedView.sort : base.sort;
  return _objectSpread(_objectSpread({}, base), {}, {
    filters: mergedFilters,
    sort
  });
};

/**
 * Build the `serverReplaceView` payload from the URL only. Does not read Redux
 * `indexQuery.filters` — same idea as a fresh saved-filter apply (no prior cabinet
 * selections merged in). Paging/sort come from `initialIndexQuery` only.
 *
 * @param {{
 *   location: { search?: string, hash?: string },
 *   filterDefinitions: Array<{ index?: string }>,
 *   initialIndexQuery?: Record<string, unknown>,
 *   formTypeIdentifiers?: string[],
 *   cabinetIdentifier: string,
 * }} args
 * @returns {{
 *   hasUrlFilters: boolean,
 *   replaceViewQuery: Record<string, unknown> | null,
 *   deepLinkKey: string,
 * }}
 */
export const getCabinetDeepLinkReplaceViewPayload = _ref5 => {
  let location = _ref5.location,
    filterDefinitions = _ref5.filterDefinitions,
    _ref5$initialIndexQue = _ref5.initialIndexQuery,
    initialIndexQuery = _ref5$initialIndexQue === void 0 ? {} : _ref5$initialIndexQue,
    _ref5$formTypeIdentif = _ref5.formTypeIdentifiers,
    formTypeIdentifiers = _ref5$formTypeIdentif === void 0 ? [] : _ref5$formTypeIdentif,
    cabinetIdentifier = _ref5.cabinetIdentifier,
    savedViews = _ref5.savedViews;
  const locationSearch = getCabinetLocationSearch(location);
  const savedViewParams = getCabinetSavedViewParamsFromSearch(locationSearch);
  if (savedViewParams !== null && savedViewParams !== void 0 && savedViewParams.filterId) {
    const deepLinkKey = "".concat(cabinetIdentifier, "|saved|").concat(savedViewParams.filterId);
    const row = resolveCabinetSavedViewRow(savedViews, cabinetIdentifier, savedViewParams.filterId);
    if (row) {
      return {
        hasUrlFilters: true,
        replaceViewQuery: buildCabinetReplaceViewQuery({
          indexQuery: undefined,
          initialIndexQuery,
          savedView: row,
          formTypeIdentifiers,
          cabinetIdentifier,
          filterDefinitions,
          mergeExistingCabinetFilters: false
        }),
        deepLinkKey
      };
    }
    return {
      hasUrlFilters: true,
      replaceViewQuery: null,
      deepLinkKey
    };
  }
  const searchFilterPatch = getCabinetFiltersFromSearch(locationSearch, filterDefinitions);
  const hasUrlFilters = Object.keys(searchFilterPatch).length > 0;
  const replaceViewQuery = hasUrlFilters ? buildCabinetReplaceViewQuery({
    indexQuery: undefined,
    initialIndexQuery,
    savedView: {
      filters: searchFilterPatch
    },
    formTypeIdentifiers,
    cabinetIdentifier,
    filterDefinitions,
    mergeExistingCabinetFilters: false
  }) : null;
  const deepLinkKey = hasUrlFilters ? "".concat(cabinetIdentifier, "|").concat(locationSearch) : '';
  return {
    hasUrlFilters,
    replaceViewQuery,
    deepLinkKey
  };
};

/**
 * True while Redux `currentCabinet` has not caught up to the URL cabinet, but the hash
 * already contains at least one known cabinet filter param. In that window we must not
 * call bare `serverIndex` or we GET /forms twice (bare load, then deep link).
 */
export const shouldDeferBareCabinetIndexForDeepLink = _ref6 => {
  let location = _ref6.location,
    filterDefinitions = _ref6.filterDefinitions,
    currentCabinet = _ref6.currentCabinet,
    urlCabinetIdentifier = _ref6.urlCabinetIdentifier,
    updatedFormType = _ref6.updatedFormType,
    _ref6$formTypeIdentif = _ref6.formTypeIdentifiers,
    formTypeIdentifiers = _ref6$formTypeIdentif === void 0 ? [] : _ref6$formTypeIdentif;
  if (!formTypeIdentifiers.length || currentCabinet === urlCabinetIdentifier || updatedFormType) {
    return false;
  }
  if (hasCabinetSavedViewDeepLink(location)) {
    return true;
  }
  return Object.keys(getCabinetFiltersFromSearch(getCabinetLocationSearch(location), filterDefinitions)).length > 0;
};
export default getCabinetFiltersFromSearch;